import axios from 'axios';
import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { organs, diseases, quiz, articles, heartDetail, miDetail, pharmaDrugs } from '../mock.js';
import {
  getCuratedDiseases,
  getDiseaseDetails,
  enrichDiseaseWithIndiaPublicHealth,
} from './diseaseApi.js';
import { books as booksSeed } from '../data/books.js';
import { DOCTOR_SPECIALIZATIONS, SEED_DOCTORS, getSeedSlots } from '../data/doctorsSeed.js';
import {
  SEED_USERS,
  SEED_LMS_COURSES,
  SEED_LMS_SUBMISSIONS,
  SEED_LMS_ANNOUNCEMENTS,
  SEED_LMS_TIMETABLE,
} from '../data/adminSeed.js';
import { auth, db, firebaseConfigured } from './firebase.js';
import { recordNewDrugSaved } from './drugCounter.js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
const USE_MOCK_API = process.env.REACT_APP_USE_MOCK_API === 'true' || !BACKEND_URL;
const USE_FIRESTORE = firebaseConfigured && process.env.REACT_APP_USE_FIRESTORE_CONTENT !== 'false';

export const api = axios.create({ baseURL: API });

api.interceptors.request.use(async (config) => {
  let token = localStorage.getItem('dp_token');
  if (auth?.currentUser) {
    token = await auth.currentUser.getIdToken();
    localStorage.setItem('dp_token', token);
  }
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const asPromise = (data) => Promise.resolve(data);
const fallback = (request, data) => request.then(r => r.data).catch(() => data);
const canUseFirestore = () => USE_FIRESTORE && db;

const withTimeout = (promise, ms = 2500) => new Promise((resolve, reject) => {
  const timer = setTimeout(() => reject(new Error('Operation timed out')), ms);
  Promise.resolve(promise).then(
    (value) => { clearTimeout(timer); resolve(value); },
    (error) => { clearTimeout(timer); reject(error); },
  );
});

const normalizeSlug = (value) => (value || '')
  .toString()
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

const deepCleanPayload = (obj) => {
  if (obj === null || obj === undefined) return null;
  if (Array.isArray(obj)) {
    return obj
      .filter((item) => item !== undefined)
      .map((item) => (typeof item === 'object' && item !== null ? deepCleanPayload(item) : item));
  }
  if (typeof obj === 'object') {
    if (obj.constructor && obj.constructor.name !== 'Object') {
      return obj;
    }
    const clean = {};
    for (const [k, v] of Object.entries(obj)) {
      if (v !== undefined) {
        clean[k] = typeof v === 'object' && v !== null ? deepCleanPayload(v) : v;
      }
    }
    return clean;
  }
  return obj;
};

const cleanPayload = (payload) => deepCleanPayload(payload);

const organDetails = {
  heart: heartDetail,
};

const diseaseDetails = {
  'acute-mi': miDetail,
};

const enrichOrgan = (organ) => ({
  location: 'Study note placeholder for local frontend preview. Backend content can replace this when connected.',
  shape: 'Content available from the live API or admin panel.',
  size: 'Content available from the live API or admin panel.',
  weight: 'Content available from the live API or admin panel.',
  gross: organ.desc || 'Overview content is available in preview mode.',
  bloodSupply: 'Add full anatomy notes from the admin panel after backend deployment.',
  venous: 'Add venous drainage notes from the admin panel after backend deployment.',
  nerve: 'Add nerve supply notes from the admin panel after backend deployment.',
  embryology: 'Add embryology notes from the admin panel after backend deployment.',
  histology: 'Add histology notes from the admin panel after backend deployment.',
  physiology: ['Preview physiology note.', 'Connect the backend to load full content.'],
  clinical: ['Preview clinical correlation.', 'Admin-managed content appears here in production.'],
  redFlags: ['This is educational preview content only.'],
  mnemonic: 'Preview mnemonic content.',
  ...organ,
  ...(organDetails[organ.slug] || {}),
});

const enrichDisease = (disease) => ({
  altNames: [],
  simple: disease.short || 'Preview disease summary.',
  medical: disease.short || 'Preview medical definition.',
  epidemiology: 'Preview epidemiology content. Connect the backend for full notes.',
  causes: ['Preview cause item.'],
  riskFactors: ['Preview risk factor item.'],
  pathophysiology: 'Preview pathophysiology content.',
  symptoms: ['Preview symptom item.'],
  redFlags: disease.emergency ? ['Emergency condition. Seek urgent care in real-life situations.'] : [],
  diagnosis: ['Preview investigation item.'],
  differential: ['Preview differential item.'],
  management: ['Preview management principle.'],
  complications: ['Preview complication item.'],
  prognosis: 'Preview prognosis content.',
  mnemonic: 'Preview mnemonic content.',
  ...disease,
  ...(diseaseDetails[disease.slug] || {}),
});

const enrichArticle = (article) => ({
  author: { name: 'Dr. Pulse Editorial', role: 'Editorial Team', initials: 'DP' },
  category: 'General',
  readTime: 5,
  publishedAt: new Date().toISOString().slice(0, 10),
  likes: 0,
  adminBoost: 0,
  shares: 0,
  comments: 0,
  commentsCount: 0,
  excerpt: '',
  content: [],
  ...article,
});

const enrichQuiz = (item, index = 0) => ({
  id: item.id || `starter-${index}`,
  question: item.question || item.q || '',
  options: Array.isArray(item.options) ? item.options : ['', '', '', ''],
  answer: Number.isInteger(item.answer) ? item.answer : Number(item.answer || 0),
  explain: item.explain || item.explanation || '',
  category: item.category || 'General',
  difficulty: item.difficulty || 'Beginner',
  active: item.active !== false,
  ...item,
});

const enrichBook = (item, index = 0) => {
  // Download URLs are deliberately removed from every public book object.
  // Admin views merge the protected value from the payment API separately.
  const { pdfUrl: _pdfUrl, downloadUrl: _downloadUrl, ...publicItem } = item || {};
  const currentRating = Number(publicItem.rating || 0);
  const isOriginal = ['original', 'own'].includes((publicItem.libraryType || publicItem.sourceType || '').toLowerCase());

  // Guarantee ratings between 4.6 and 4.9 for original medical PDFs so none are below 4.4
  let safeRating = currentRating;
  if (isOriginal && (currentRating < 4.4 || currentRating > 5.0)) {
    const seed = String(publicItem.id || publicItem.slug || publicItem.title || publicItem.name || index);
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      hash = ((hash << 5) - hash) + seed.charCodeAt(i);
      hash |= 0;
    }
    const choices = [4.7, 4.8, 4.9, 4.6, 4.8, 4.7, 4.9];
    safeRating = choices[Math.abs(hash) % choices.length];
  }

  return {
    ...publicItem,
    id: publicItem.id || `starter-book-${index}`,
    slug: publicItem.slug || '',
    title: publicItem.title || publicItem.name || '',
    author: publicItem.author || '',
    edition: publicItem.edition || '',
    category: publicItem.category || 'General',
    description: publicItem.description || '',
    price: publicItem.price || '',
    image: publicItem.image || '',
    amazonUrl: publicItem.amazonUrl || publicItem.url || '',
    kindleUrl: publicItem.kindleUrl || '',
    productUrl: publicItem.productUrl || '',
    detailsUrl: publicItem.detailsUrl || '',
    libraryType: publicItem.libraryType || publicItem.sourceType || 'external',
    amountPaise: Number(publicItem.amountPaise || 0),
    currency: publicItem.currency || 'INR',
    paymentEnabled: publicItem.paymentEnabled === true,
    active: publicItem.active !== false,
    rating: safeRating,
    reviewCount: Number(publicItem.reviewCount || 0),
    pdfUrl: '',
  };
};

const enrichDrug = (d) => ({
  id: d.id || normalizeSlug(d.name),
  slug: d.slug || d.id || normalizeSlug(d.name),
  name: d.name,
  genericName: d.genericName || d.name,
  brandName: d.brandName || null,
  class: d.class || 'Therapeutic Agent',
  category: d.category || 'General',
  desc: d.desc || '',
  dose: d.dose || '',
  caution: d.caution || null,
  routes: Array.isArray(d.routes) ? d.routes : ['Oral'],
  ...d,
});

const listFirestoreBooks = async (includeInactive = false) => {
  const fallbackData = booksSeed.map(enrichBook);
  if (!canUseFirestore()) return includeInactive ? fallbackData : fallbackData.filter((b) => b.active !== false);

  try {
    const snapshot = await withTimeout(getDocs(collection(db, 'books')), 2500);
    if (!snapshot || snapshot.empty) return includeInactive ? fallbackData : fallbackData.filter((b) => b.active !== false);
    const items = snapshot.docs.map((item, index) => enrichBook({ id: item.id, ...item.data() }, index));
    return includeInactive ? items : items.filter((item) => item.active !== false);
  } catch (e) {
    return includeInactive ? fallbackData : fallbackData.filter((b) => b.active !== false);
  }
};

const listFirestoreContent = async (collectionName, fallbackData, enrich) => {
  if (!canUseFirestore()) return fallbackData.map(enrich);

  try {
    const snapshot = await withTimeout(getDocs(collection(db, collectionName)), 6000);
    if (!snapshot || snapshot.empty) return fallbackData.map(enrich);
    return snapshot.docs.map((item) => enrich({ id: item.id, ...item.data() }));
  } catch (e) {
    return fallbackData.map(enrich);
  }
};

const listFirestoreQuizzes = async (includeInactive = false) => {
  const fallbackData = quiz.map(enrichQuiz);
  if (!canUseFirestore()) return fallbackData;

  try {
    const snapshot = await withTimeout(getDocs(query(collection(db, 'quizzes'), orderBy('updated_at', 'desc'))), 2500);
    if (!snapshot || snapshot.empty) return fallbackData;
    const items = snapshot.docs.map((item, index) => enrichQuiz({ id: item.id, ...item.data() }, index));
    return includeInactive ? items : items.filter((item) => item.active !== false);
  } catch (e) {
    return fallbackData;
  }
};

const getFirestoreContentBySlug = async (collectionName, slug, fallbackData, enrich) => {
  if (!canUseFirestore()) {
    const fallbackItem = fallbackData.find((item) => item.slug === slug);
    return fallbackItem ? enrich(fallbackItem) : null;
  }

  try {
    const q = query(collection(db, collectionName), where('slug', '==', slug), limit(1));
    const snapshot = await withTimeout(getDocs(q), 2500);
    if (snapshot && !snapshot.empty) {
      const item = snapshot.docs[0];
      return enrich({ id: item.id, ...item.data() });
    }
  } catch (e) {
    // Fall through to local content.
  }

  const fallbackItem = fallbackData.find((item) => item.slug === slug);
  return fallbackItem ? enrich(fallbackItem) : null;
};

const saveFirestoreContent = async (collectionName, payload, id) => {
  if (!canUseFirestore()) throw new Error('Firestore is not configured');

  const slug = normalizeSlug(payload.slug || id);
  if (!slug) throw new Error('Slug is required');

  const ref = doc(db, collectionName, id || slug);
  const data = cleanPayload({
    ...payload,
    id: id || slug,
    slug,
    updated_at: serverTimestamp(),
    created_at: payload.created_at || serverTimestamp(),
  });
  if (collectionName === 'books') {
    data.pdfUrl = deleteField();
    data.downloadUrl = deleteField();
  }

  await withTimeout(setDoc(ref, data, { merge: true }), 10000);
  return { id: ref.id, ...payload, slug };
};

const deleteFirestoreContent = async (collectionName, id) => {
  if (!canUseFirestore()) throw new Error('Firestore is not configured');
  await deleteDoc(doc(db, collectionName, id));
  return { success: true };
};

let drugsCache = {
  data: null,
  timestamp: 0,
};
const DRUGS_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

let diseasesCache = {
  data: null,
  timestamp: 0,
};
const DISEASES_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

export const contentApi = {
  listOrgans: () => {
    const data = organs.map(enrichOrgan);
    if (canUseFirestore()) return listFirestoreContent('organs', organs, enrichOrgan);
    return USE_MOCK_API ? asPromise(data) : fallback(api.get('/content/organs'), data);
  },
  getOrgan: (slug) => {
    const organ = organs.find(o => o.slug === slug);
    const data = organ ? enrichOrgan(organ) : null;
    if (canUseFirestore()) return getFirestoreContentBySlug('organs', slug, organs, enrichOrgan);
    return USE_MOCK_API ? asPromise(data) : fallback(api.get(`/content/organs/${slug}`), data);
  },
  listDiseases: async (forceRefresh = false) => {
    if (!forceRefresh && diseasesCache.data && (Date.now() - diseasesCache.timestamp < DISEASES_CACHE_DURATION)) {
      return diseasesCache.data;
    }
    const fallbackData = getCuratedDiseases();
    let firestoreList = [];
    if (canUseFirestore()) {
      try {
        firestoreList = await listFirestoreContent('diseases', fallbackData, enrichDiseaseWithIndiaPublicHealth);
      } catch (e) {
        console.warn('Firestore disease list fetch warning:', e);
      }
    }
    let local = [];
    try {
      local = JSON.parse(localStorage.getItem('dp_cached_diseases') || '[]').map(enrichDiseaseWithIndiaPublicHealth);
    } catch { }

    const map = new Map();
    // 1. Seed with Firestore entries
    for (const d of firestoreList) {
      const key = (d.slug || d.id || d.name || '').toLowerCase();
      if (key) map.set(key, d);
    }
    // 2. Merge local storage entries
    for (const d of local) {
      const key = (d.slug || d.id || d.name || '').toLowerCase();
      if (key && !map.has(key)) map.set(key, d);
    }
    // 3. Merge base curated public health diseases
    for (const d of fallbackData) {
      const key = (d.slug || d.id || d.name || '').toLowerCase();
      if (key && !map.has(key)) map.set(key, d);
    }

    const merged = Array.from(map.values());
    diseasesCache = { data: merged, timestamp: Date.now() };
    return merged;
  },
  getDisease: async (slug) => {
    if (!slug) return null;
    const cleanSlug = normalizeSlug(slug);

    // 1. Try local storage cache
    try {
      const local = JSON.parse(localStorage.getItem('dp_cached_diseases') || '[]');
      const found = local.find(d => d.slug === cleanSlug || d.id === cleanSlug);
      if (found) return enrichDiseaseWithIndiaPublicHealth(found);
    } catch { }

    // 2. Try Firestore
    if (canUseFirestore()) {
      try {
        const item = await getFirestoreContentBySlug('diseases', cleanSlug, [], enrichDiseaseWithIndiaPublicHealth);
        if (item) return item;
      } catch { }
    }

    // 3. Try curated / online monograph from diseaseApi
    const detail = await getDiseaseDetails(cleanSlug);
    if (detail) {
      contentApi.saveDisease(detail).catch(() => {});
      return detail;
    }

    return null;
  },
  saveDisease: async (disease) => {
    if (!disease) return;
    const cleanSlug = normalizeSlug(disease.slug || disease.name);
    const enriched = enrichDiseaseWithIndiaPublicHealth({ ...disease, slug: cleanSlug, id: cleanSlug });
    const payload = {
      ...enriched,
      slug: cleanSlug,
      id: cleanSlug,
      updated_at: new Date().toISOString(),
    };

    if (canUseFirestore()) {
      try {
        await saveFirestoreContent('diseases', payload, cleanSlug);
      } catch (e) {
        console.warn('Firestore disease save warning:', e);
      }
    }

    try {
      const existing = JSON.parse(localStorage.getItem('dp_cached_diseases') || '[]');
      const filtered = existing.filter((d) => d.slug !== cleanSlug && d.id !== cleanSlug);
      localStorage.setItem('dp_cached_diseases', JSON.stringify([payload, ...filtered].slice(0, 200)));
    } catch { }

    // Invalidate in-memory cache so fresh data shows immediately
    diseasesCache = { data: null, timestamp: 0 };
    return payload;
  },
  listArticles: async () => {
    const data = articles.map(enrichArticle);
    const result = canUseFirestore()
      ? await listFirestoreContent('articles', articles, enrichArticle)
      : (USE_MOCK_API ? await asPromise(data) : await fallback(api.get('/content/articles'), data));
    return [...result].sort((a, b) => new Date(b.publishedAt || 0) - new Date(a.publishedAt || 0));
  },
  listBooks: async () => {
    const data = booksSeed.map(enrichBook).filter((b) => b.active !== false);
    if (canUseFirestore()) return listFirestoreBooks(false);
    if (USE_MOCK_API) return asPromise(data);
    const result = await fallback(api.get('/content/books'), data);
    return (result || []).map(enrichBook).filter((b) => b.active !== false);
  },
  getArticle: (slug) => {
    const article = articles.find(a => a.slug === slug);
    const data = article ? enrichArticle(article) : null;
    if (canUseFirestore()) return getFirestoreContentBySlug('articles', slug, articles, enrichArticle);
    return USE_MOCK_API ? asPromise(data) : fallback(api.get(`/content/articles/${slug}`), data);
  },
  searchPharma: async (query) => {
    return api.get('/pharma/search', { params: { q: query } })
      .then((res) => res.data)
      .catch(() => []);
  },
  listDrugs: async (forceRefresh = false, includeClutter = false) => {
    if (!forceRefresh && drugsCache.data && (Date.now() - drugsCache.timestamp < DRUGS_CACHE_DURATION)) {
      return drugsCache.data;
    }
    let firestoreList = [];
    if (canUseFirestore()) {
      try {
        // Query specifically for Tata 1mg verified saved drugs in Firestore
        // Super-fast indexed query that bypasses legacy clutter documents
        const qTata = query(collection(db, 'drugs'), where('isTata1mg', '==', true));
        const snapshot = await withTimeout(getDocs(qTata), 6000);
        if (snapshot && !snapshot.empty) {
          firestoreList = snapshot.docs.map((docSnap) => enrichDrug({ id: docSnap.id, ...docSnap.data() }));
        }
      } catch (e) {
        console.warn('Firestore Tata drug list fetch warning:', e);
      }
    }
    let local = [];
    try {
      local = JSON.parse(localStorage.getItem('dp_cached_drugs') || '[]').map(enrichDrug);
    } catch { }

    const merged = [];
    const seen = new Set();

    // 1. Shared Firestore database items (accessible to all logged in or guest users)
    for (const d of firestoreList) {
      const key = (d.slug || d.id || d.name || '').toLowerCase();
      if (key && key !== '_metadata_stats' && !seen.has(key)) {
        merged.push(d);
        seen.add(key);
      }
    }

    // 2. Local cached items
    for (const d of local) {
      if (d && (d.isTata1mg || String(d.id || '').startsWith('tata1mg'))) {
        const key = (d.slug || d.id || d.name || '').toLowerCase();
        if (key && key !== '_metadata_stats' && !seen.has(key)) {
          merged.push(d);
          seen.add(key);
        }
      }
    }

    // Keep localStorage in sync with cloud database
    try {
      if (typeof localStorage !== 'undefined' && merged.length > 0) {
        localStorage.setItem('dp_cached_drugs', JSON.stringify(merged));
        localStorage.setItem('dp_total_drugs_count', String(merged.length));
      }
    } catch {}

    drugsCache = { data: merged, timestamp: Date.now() };
    return merged.filter((d) => !d.name?.includes('1,4-benzoquinone'));
  },
  clearAllDrugs: async () => {
    drugsCache = { data: [], timestamp: Date.now() };
    if (canUseFirestore()) {
      try {
        const qTata = query(collection(db, 'drugs'), where('isTata1mg', '==', true));
        const snap = await getDocs(qTata);
        const promises = snap.docs.map(async (docSnap) => {
          try {
            await deleteDoc(docSnap.ref);
          } catch {
            await setDoc(docSnap.ref, { isTata1mg: false, active: false }, { merge: true });
          }
        });
        await Promise.all(promises);
        const statsRef = doc(db, 'drugs', '_metadata_stats');
        await setDoc(statsRef, { totalCount: 0, savedCount: 0, updatedAt: serverTimestamp() }, { merge: true }).catch(() => {});
      } catch (e) {
        console.warn('Could not clear Firestore drugs:', e);
      }
    }
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('dp_cached_drugs');
        localStorage.removeItem('dp_known_drug_slugs');
        localStorage.removeItem('dp_total_drugs_count');
      }
    } catch {}
    return true;
  },
  getDrug: async (slug) => {
    const norm = normalizeSlug(slug);
    if (!norm) return null;
    if (canUseFirestore()) {
      try {
        const docRef = doc(db, 'drugs', norm);
        const snap = await withTimeout(getDoc(docRef), 5000);
        if (snap && snap.exists()) {
          return enrichDrug({ id: snap.id, ...snap.data() });
        }
      } catch (e) {
        console.warn('Firestore getDrug error:', e);
      }
    }
    const local = JSON.parse(localStorage.getItem('dp_cached_drugs') || '[]');
    const foundLocal = local.find((d) => d.slug === norm || d.id === norm);
    if (foundLocal) return enrichDrug(foundLocal);
    return null;
  },
  saveDrug: async (drug) => {
    if (!drug) throw new Error('A medicine is required.');
    const cleanSlug = normalizeSlug(drug.slug || drug.genericName || drug.name);
    if (!cleanSlug || cleanSlug === '_metadata_stats') throw new Error('A valid medicine name is required.');
    const now = new Date().toISOString();
    const payload = {
      ...drug,
      slug: cleanSlug,
      id: cleanSlug,
      isTata1mg: true,
      updated_at: now,
      created_at: drug.created_at || now,
    };
    // Cache immediately so the result and its detail page work while the
    // database write is pending, offline, or rejected.
    try {
      const existing = JSON.parse(localStorage.getItem('dp_cached_drugs') || '[]');
      const filtered = existing.filter((d) => d.slug !== cleanSlug);
      localStorage.setItem('dp_cached_drugs', JSON.stringify([payload, ...filtered].slice(0, 200)));
    } catch { }

    drugsCache = { data: null, timestamp: 0 };
    if (!canUseFirestore()) {
      throw new Error('Shared medicine storage is not configured. Results are available on this device only.');
    }

    const ref = doc(db, 'drugs', cleanSlug);
    const data = deepCleanPayload({ ...payload, updated_at: serverTimestamp() });
    // Propagate failures so callers cannot report a browser cache as a
    // successful shared database save. Stable document IDs prevent duplicates.
    await withTimeout(setDoc(ref, data, { merge: true }), 10000);

    // Only count confirmed database writes.
    try {
      await withTimeout(recordNewDrugSaved(payload), 2500);
    } catch { }

    // Invalidate in-memory cache so fresh data shows immediately
    drugsCache = { data: null, timestamp: 0 };
    return payload;
  },
};


export const quizApi = {
  listQuizzes: (options = {}) => listFirestoreQuizzes(!!options.includeInactive),
};

export const adminApi = {
  createOrgan: (payload) => canUseFirestore() ? saveFirestoreContent('organs', payload) : api.post('/admin/organs', payload).then(r => r.data),
  updateOrgan: (id, payload) => canUseFirestore() ? saveFirestoreContent('organs', payload, id) : api.put(`/admin/organs/${id}`, payload).then(r => r.data),
  deleteOrgan: (id) => canUseFirestore() ? deleteFirestoreContent('organs', id) : api.delete(`/admin/organs/${id}`).then(r => r.data),
  createDisease: (payload) => canUseFirestore() ? saveFirestoreContent('diseases', payload, payload.slug || normalizeSlug(payload.name)) : contentApi.saveDisease(payload),
  updateDisease: (id, payload) => canUseFirestore() ? saveFirestoreContent('diseases', payload, id) : contentApi.saveDisease(payload),
  deleteDisease: async (id) => {
    if (canUseFirestore()) {
      await deleteFirestoreContent('diseases', id);
    }
    try {
      const existing = JSON.parse(localStorage.getItem('dp_cached_diseases') || '[]');
      const filtered = existing.filter((d) => d.id !== id && d.slug !== id && d.name !== id);
      localStorage.setItem('dp_cached_diseases', JSON.stringify(filtered));
    } catch { }
    diseasesCache = { data: null, timestamp: 0 };
    return { success: true };
  },
  createArticle: (payload) => canUseFirestore() ? saveFirestoreContent('articles', payload) : api.post('/admin/articles', payload).then(r => r.data),
  updateArticle: (id, payload) => canUseFirestore() ? saveFirestoreContent('articles', payload, id) : api.put(`/admin/articles/${id}`, payload).then(r => r.data),
  deleteArticle: (id) => canUseFirestore() ? deleteFirestoreContent('articles', id) : api.delete(`/admin/articles/${id}`).then(r => r.data),
  listDrugs: () => contentApi.listDrugs(true, true),
  createDrug: (payload) => canUseFirestore() ? saveFirestoreContent('drugs', payload, payload.slug || normalizeSlug(payload.name)) : contentApi.saveDrug(payload),
  updateDrug: (id, payload) => canUseFirestore() ? saveFirestoreContent('drugs', payload, id) : contentApi.saveDrug(payload),
  deleteDrug: async (id) => {
    if (canUseFirestore()) {
      await deleteFirestoreContent('drugs', id);
    }
    try {
      const existing = JSON.parse(localStorage.getItem('dp_cached_drugs') || '[]');
      const filtered = existing.filter((d) => d.id !== id && d.slug !== id && d.name !== id);
      localStorage.setItem('dp_cached_drugs', JSON.stringify(filtered));
    } catch { }
    drugsCache = { data: null, timestamp: 0 };
    return { success: true };
  },
  listQuizzes: () => listFirestoreQuizzes(true),
  listBooks: async () => {
    if (canUseFirestore()) return listFirestoreBooks(true);
    const data = booksSeed.map(enrichBook);
    const result = await fallback(api.get('/content/books'), data);
    return (result || []).map(enrichBook);
  },
  createBook: (payload) => canUseFirestore() ? saveFirestoreContent('books', payload) : api.post('/admin/books', payload).then(r => r.data),
  updateBook: (id, payload) => canUseFirestore() ? saveFirestoreContent('books', payload, id) : api.put(`/admin/books/${id}`, payload).then(r => r.data),
  deleteBook: (id) => canUseFirestore() ? deleteFirestoreContent('books', id) : api.delete(`/admin/books/${id}`).then(r => r.data),
  listPaymentProducts: () => api.get('/admin/payment-products').then(r => r.data),
  savePaymentProduct: (bookId, payload) => api.put(`/admin/payment-products/${encodeURIComponent(bookId)}`, payload).then(r => r.data),
  deletePaymentProduct: (bookId) => api.delete(`/admin/payment-products/${encodeURIComponent(bookId)}`).then(r => r.data),
  createQuiz: (payload) => canUseFirestore() ? saveFirestoreContent('quizzes', payload) : api.post('/admin/quizzes', payload).then(r => r.data),
  updateQuiz: (id, payload) => canUseFirestore() ? saveFirestoreContent('quizzes', payload, id) : api.put(`/admin/quizzes/${id}`, payload).then(r => r.data),
  deleteQuiz: (id) => canUseFirestore() ? deleteFirestoreContent('quizzes', id) : api.delete(`/admin/quizzes/${id}`).then(r => r.data),
  // Admin-only like boost — a separate counter from the one-per-user `likes`
  // total, so admins can add as many as they want without the per-user lock.
  // Enforced server-side: firestore.rules only lets isAdmin() touch adminBoost.
  boostArticleLikes: (articleId, delta = 1) => {
    if (!canUseFirestore()) throw new Error('Firestore is not configured');
    return updateDoc(doc(db, 'articles', articleId), { adminBoost: increment(delta) });
  },

  // --- USERS & DOCTORS MANAGEMENT ---
  listAllUsers: async () => {
    let firestoreUsers = [];
    if (canUseFirestore()) {
      try {
        const snap = await withTimeout(getDocs(collection(db, 'users')), 4000);
        firestoreUsers = snap ? snap.docs.map((d) => ({ id: d.id, ...d.data() })) : [];
      } catch (err) {
        console.warn('Could not fetch Firestore users:', err?.message || err);
      }
    }

    // 1. Gather all local users from drpulse_registered_users
    let localUsers = [];
    try {
      localUsers = JSON.parse(localStorage.getItem('drpulse_registered_users') || '[]');
    } catch { }

    // 2. Also scan localStorage keys for dp_profile_* (only valid registered accounts with email)
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('dp_profile_')) {
          const raw = localStorage.getItem(key);
          if (raw) {
            const p = JSON.parse(raw);
            const uid = key.replace('dp_profile_', '');
            const email = (p.email || '').toLowerCase().trim();
            // ONLY consider if p has a valid email address and is not admin or empty session
            if (
              p &&
              email &&
              email.includes('@') &&
              email !== 'admin@gmail.com' &&
              !localUsers.some(
                (u) =>
                  u.id === uid ||
                  (u.email && u.email.toLowerCase().trim() === email)
              )
            ) {
              localUsers.push({ id: uid, ...p });
            }
          }
        }
      }
    } catch { }

    let localEdits = {};
    try {
      localEdits = JSON.parse(localStorage.getItem('drpulse_admin_users_overrides') || '{}');
    } catch { }

    const combined = [...firestoreUsers];
    const seenIds = new Set(firestoreUsers.map((u) => u.id));
    const seenEmails = new Set(firestoreUsers.map((u) => (u.email || '').toLowerCase().trim()));

    localUsers.forEach((lu) => {
      const email = (lu.email || '').toLowerCase().trim();
      if (!seenIds.has(lu.id) && !seenEmails.has(email)) {
        combined.push(lu);
        seenIds.add(lu.id);
        if (email) seenEmails.add(email);
      }
    });

    // PURE REAL USERS - ZERO MOCK USERS! ZERO GHOST SESSIONS!
    return combined
      .filter((u) => {
        const email = (u.email || '').toLowerCase().trim();
        // Must be a valid email and never admin
        if (!email || !email.includes('@') || email === 'admin@gmail.com') return false;
        // Never show placeholder "Member" accounts that have no identity
        if ((!u.full_name || u.full_name === 'Member') && !u.email) return false;
        return true;
      })
      .map((u) => {
        const overrides = localEdits[u.id] || {};
        return { ...u, ...overrides };
      });
  },

  updateUserRole: async (userId, newRole) => {
    if (canUseFirestore() && !userId.startsWith('user-')) {
      try {
        await updateDoc(doc(db, 'users', userId), {
          role: newRole,
          is_approved: newRole === 'student' || newRole === 'doctor' || newRole === 'general',
          verification_status: 'approved',
          updated_at: serverTimestamp(),
        });
      } catch (e) {
        console.warn('Firestore user role update failed:', e);
      }
    }
    let targetEmail = '';
    try {
      const regList = JSON.parse(localStorage.getItem('drpulse_registered_users') || '[]');
      const target = regList.find(u => u.id === userId);
      if (target?.email) targetEmail = target.email.toLowerCase().trim();
    } catch {}

    try {
      const localEdits = JSON.parse(localStorage.getItem('drpulse_admin_users_overrides') || '{}');
      const updateObj = {
        ...(localEdits[userId] || {}),
        role: newRole,
        is_approved: newRole === 'student' || newRole === 'doctor' || newRole === 'general',
        verification_status: 'approved',
      };
      localEdits[userId] = updateObj;
      if (targetEmail) localEdits[targetEmail] = updateObj;
      localStorage.setItem('drpulse_admin_users_overrides', JSON.stringify(localEdits));
    } catch { }

    try {
      const regList = JSON.parse(localStorage.getItem('drpulse_registered_users') || '[]');
      const updatedRegList = regList.map((u) => {
        if (u.id === userId || (targetEmail && u.email && u.email.toLowerCase().trim() === targetEmail)) {
          return {
            ...u,
            role: newRole,
            is_approved: newRole === 'student' || newRole === 'doctor' || newRole === 'general',
            verification_status: 'approved',
          };
        }
        return u;
      });
      localStorage.setItem('drpulse_registered_users', JSON.stringify(updatedRegList));
    } catch { }

    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('dp_profile_')) {
          const raw = localStorage.getItem(key);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (key === `dp_profile_${userId}` || (targetEmail && parsed.email && parsed.email.toLowerCase().trim() === targetEmail)) {
              parsed.role = newRole;
              parsed.is_approved = true;
              parsed.verification_status = 'approved';
              localStorage.setItem(key, JSON.stringify(parsed));
            }
          }
        }
      }
    } catch {}

    return { success: true, role: newRole };
  },

  verifyDoctor: async (userId, isApproved, verificationStatus = 'approved', note = '') => {
    if (canUseFirestore() && !userId.startsWith('user-')) {
      try {
        await updateDoc(doc(db, 'users', userId), {
          role: isApproved ? 'doctor' : 'general',
          is_approved: isApproved,
          verification_status: verificationStatus,
          verification_notes: note,
          updated_at: serverTimestamp(),
        });
      } catch (e) {
        console.warn('Firestore doctor verification update failed:', e);
      }
    }

    let targetEmail = '';
    try {
      const regList = JSON.parse(localStorage.getItem('drpulse_registered_users') || '[]');
      const target = regList.find(u => u.id === userId);
      if (target?.email) targetEmail = target.email.toLowerCase().trim();
    } catch {}

    try {
      const localEdits = JSON.parse(localStorage.getItem('drpulse_admin_users_overrides') || '{}');
      const updateObj = {
        ...(localEdits[userId] || {}),
        role: isApproved ? 'doctor' : 'general',
        is_approved: isApproved,
        verification_status: verificationStatus,
        verification_notes: note,
      };
      localEdits[userId] = updateObj;
      if (targetEmail) localEdits[targetEmail] = updateObj;
      localStorage.setItem('drpulse_admin_users_overrides', JSON.stringify(localEdits));
    } catch { }

    // Update drpulse_registered_users
    try {
      const regList = JSON.parse(localStorage.getItem('drpulse_registered_users') || '[]');
      const updatedRegList = regList.map((u) => {
        if (u.id === userId || (targetEmail && u.email && u.email.toLowerCase().trim() === targetEmail)) {
          return {
            ...u,
            role: isApproved ? 'doctor' : 'general',
            is_approved: isApproved,
            verification_status: verificationStatus,
            verification_notes: note,
          };
        }
        return u;
      });
      localStorage.setItem('drpulse_registered_users', JSON.stringify(updatedRegList));
    } catch { }

    // Update individual profile caches
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('dp_profile_')) {
          const raw = localStorage.getItem(key);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (key === `dp_profile_${userId}` || (targetEmail && parsed.email && parsed.email.toLowerCase().trim() === targetEmail)) {
              parsed.is_approved = isApproved;
              parsed.verification_status = verificationStatus;
              parsed.verification_notes = note;
              if (isApproved) parsed.role = 'doctor';
              localStorage.setItem(key, JSON.stringify(parsed));
            }
          }
        }
      }
    } catch { }

    return { success: true, is_approved: isApproved, verification_status: verificationStatus };
  },

  verifyStudent: async (userId, isApproved, verificationStatus = 'approved', note = '') => {
    if (canUseFirestore() && !userId.startsWith('user-')) {
      try {
        await updateDoc(doc(db, 'users', userId), {
          role: isApproved ? 'student' : 'general',
          is_approved: isApproved,
          verification_status: verificationStatus,
          verification_notes: note,
          updated_at: serverTimestamp(),
        });
      } catch (e) {
        console.warn('Firestore student verification update failed:', e);
      }
    }

    let targetEmail = '';
    try {
      const regList = JSON.parse(localStorage.getItem('drpulse_registered_users') || '[]');
      const target = regList.find(u => u.id === userId);
      if (target?.email) targetEmail = target.email.toLowerCase().trim();
    } catch {}

    try {
      const localEdits = JSON.parse(localStorage.getItem('drpulse_admin_users_overrides') || '{}');
      const updateObj = {
        ...(localEdits[userId] || {}),
        role: isApproved ? 'student' : 'general',
        is_approved: isApproved,
        verification_status: verificationStatus,
        verification_notes: note,
      };
      localEdits[userId] = updateObj;
      if (targetEmail) localEdits[targetEmail] = updateObj;
      localStorage.setItem('drpulse_admin_users_overrides', JSON.stringify(localEdits));
    } catch { }

    // Update drpulse_registered_users
    try {
      const regList = JSON.parse(localStorage.getItem('drpulse_registered_users') || '[]');
      const updatedRegList = regList.map((u) => {
        if (u.id === userId || (targetEmail && u.email && u.email.toLowerCase().trim() === targetEmail)) {
          return {
            ...u,
            role: isApproved ? 'student' : 'general',
            is_approved: isApproved,
            verification_status: verificationStatus,
            verification_notes: note,
          };
        }
        return u;
      });
      localStorage.setItem('drpulse_registered_users', JSON.stringify(updatedRegList));
    } catch { }

    // Update individual profile caches
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('dp_profile_')) {
          const raw = localStorage.getItem(key);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (key === `dp_profile_${userId}` || (targetEmail && parsed.email && parsed.email.toLowerCase().trim() === targetEmail)) {
              parsed.is_approved = isApproved;
              parsed.verification_status = verificationStatus;
              parsed.verification_notes = note;
              if (isApproved) parsed.role = 'student';
              localStorage.setItem(key, JSON.stringify(parsed));
            }
          }
        }
      }
    } catch { }

    return { success: true, is_approved: isApproved, verification_status: verificationStatus };
  },

  deleteUser: async (userId) => {
    if (canUseFirestore() && !userId.startsWith('user-')) {
      try {
        await deleteDoc(doc(db, 'users', userId));
      } catch (e) {
        console.warn('Firestore user delete error:', e);
      }
    }
    try {
      const overrides = JSON.parse(localStorage.getItem('drpulse_admin_users_overrides') || '{}');
      delete overrides[userId];
      localStorage.setItem('drpulse_admin_users_overrides', JSON.stringify(overrides));
    } catch { }

    try {
      const regList = JSON.parse(localStorage.getItem('drpulse_registered_users') || '[]');
      const filtered = regList.filter((u) => u.id !== userId);
      localStorage.setItem('drpulse_registered_users', JSON.stringify(filtered));
    } catch { }

    try {
      localStorage.removeItem(`dp_profile_${userId}`);
    } catch { }

    return { success: true };
  },

  // --- ALL APPOINTMENTS & CONSULTATIONS ---
  listAllAppointments: async () => {
    let list = [];
    if (canUseFirestore() && auth?.currentUser?.email?.toLowerCase().trim() === 'admin@gmail.com') {
      try {
        const snap = await withTimeout(getDocs(collection(db, 'appointments')), 2500);
        list = snap ? snap.docs.map((d) => ({ id: d.id, ...d.data() })) : [];
      } catch (err) {
        console.warn('Could not fetch appointments from Firestore:', err?.message || err);
      }
    }
    try {
      const stored = JSON.parse(localStorage.getItem('drpulse_local_appts') || '[]');
      const localMatches = stored.filter((a) => !list.some((x) => x.id === a.id));
      list = [...list, ...localMatches];
    } catch { }

    if (list.length === 0) {
      list = [
        {
          id: 'appt-demo-1',
          doctorName: 'Dr. Sarah Perera',
          doctorSpecialization: 'Cardiology',
          patientName: 'Kamal Silva',
          patientEmail: 'kamal.silva@gmail.com',
          patientPhone: '+94 77 123 4567',
          appointmentDate: '2026-10-27',
          appointmentTime: '10:00 AM - 10:30 AM',
          mode: 'audio',
          status: 'confirmed',
          reason: 'Follow-up consultation for mild exertional chest tightness and BP review.',
        },
        {
          id: 'appt-demo-2',
          doctorName: 'Dr. Aravind Sharma',
          doctorSpecialization: 'Cardiology',
          patientName: 'Sunethra Bandara',
          patientEmail: 'sunethra.b@yahoo.com',
          patientPhone: '+94 71 890 1234',
          appointmentDate: '2026-10-28',
          appointmentTime: '02:00 PM - 02:30 PM',
          mode: 'video',
          status: 'confirmed',
          reason: 'Hypertension evaluation and ECG review.',
        },
        {
          id: 'appt-demo-3',
          doctorName: 'Dr. Dilshan Senanayake',
          doctorSpecialization: 'General Surgery & Trauma',
          patientName: 'Nimal Rajapakse',
          patientEmail: 'nimal.r@gmail.com',
          patientPhone: '+94 76 555 4321',
          appointmentDate: '2026-10-24',
          appointmentTime: '04:00 PM - 04:30 PM',
          mode: 'audio',
          status: 'completed',
          reason: 'Post-op appendectomy review and suture inspection.',
          doctorNotes: 'Wound healing well. No signs of infection. Normal diet resumed.',
        },
      ];
    }
    return list.sort((a, b) => (b.appointmentDate || '').localeCompare(a.appointmentDate || ''));
  },

  // --- CAMPUS LMS MANAGEMENT ---
  // --- CAMPUS LMS MANAGEMENT ---
  listLmsCourses: async () => {
    let courses = [];
    if (canUseFirestore()) {
      try {
        const snap = await getDocs(collection(db, 'lms_courses'));
        courses = snap ? snap.docs.map((d) => ({ id: d.id, ...d.data() })) : [];
      } catch (err) {
        console.warn('Could not fetch lms_courses from Firestore:', err);
      }
    }
    try {
      const stored = localStorage.getItem('drpulse_admin_lms_courses');
      if (stored) {
        const localList = JSON.parse(stored);
        const seen = new Set(courses.map((c) => c.id));
        localList.forEach((c) => {
          if (!seen.has(c.id)) {
            courses.push(c);
            seen.add(c.id);
          }
        });
      }
    } catch { }

    // If still empty (fresh deployment or new device), seed from default curriculum
    if (courses.length === 0 && Array.isArray(SEED_LMS_COURSES) && SEED_LMS_COURSES.length > 0) {
      courses = [...SEED_LMS_COURSES];
      try {
        localStorage.setItem('drpulse_admin_lms_courses', JSON.stringify(courses));
      } catch { }
      if (canUseFirestore()) {
        SEED_LMS_COURSES.forEach((c) => {
          setDoc(doc(db, 'lms_courses', c.id), c, { merge: true }).catch(() => {});
        });
      }
    }

    // Sanitize any stale mock counts or missing sections
    return courses.map((c) => {
      const sections = Array.isArray(c.sections) && c.sections.length > 0
        ? c.sections
        : [
          {
            id: `sec-gen-${c.id || Date.now()}`,
            title: 'General',
            items: [
              {
                id: `item-ann-${c.id || Date.now()}`,
                type: 'announcement',
                title: 'Announcements',
                desc: 'General course announcements and notifications.',
              },
            ],
          },
        ];

      const enrolledUsers = Array.isArray(c.enrolled_users) ? c.enrolled_users : [];
      const enrolled = typeof c.enrolled_students === 'number' && c.enrolled_students !== 45 && c.enrolled_students !== 120
        ? c.enrolled_students
        : enrolledUsers.length;

      return {
        ...c,
        enrolled_users: enrolledUsers,
        enrolled_students: enrolled,
        modulesCount: sections.length,
        sections,
      };
    });
  },

  saveLmsCourse: async (course) => {
    const id = course.id || `course-${Date.now()}`;
    const payload = {
      ...course,
      id,
      status: course.status || 'active',
      sections: course.sections || [],
      updatedAt: new Date().toISOString(),
    };

    if (canUseFirestore()) {
      try {
        await setDoc(doc(db, 'lms_courses', id), payload, { merge: true });
      } catch (e) {
        console.warn('Firestore saveLmsCourse error:', e);
      }
    }

    try {
      const stored = JSON.parse(localStorage.getItem('drpulse_admin_lms_courses') || '[]');
      const idx = stored.findIndex((c) => c.id === id);
      let updated;
      if (idx >= 0) {
        updated = [...stored];
        updated[idx] = { ...updated[idx], ...payload };
      } else {
        updated = [payload, ...stored];
      }
      localStorage.setItem('drpulse_admin_lms_courses', JSON.stringify(updated));
    } catch { }

    return payload;
  },

  deleteLmsCourse: async (courseId) => {
    if (canUseFirestore()) {
      try {
        await deleteDoc(doc(db, 'lms_courses', courseId));
      } catch (e) {
        console.warn('Firestore deleteLmsCourse error:', e);
      }
    }
    try {
      const stored = JSON.parse(localStorage.getItem('drpulse_admin_lms_courses') || '[]');
      const updated = stored.filter((c) => c.id !== courseId);
      localStorage.setItem('drpulse_admin_lms_courses', JSON.stringify(updated));
    } catch { }
    return { success: true };
  },

  listLmsSubmissions: async () => {
    try {
      const stored = localStorage.getItem('drpulse_admin_lms_submissions');
      if (stored) return JSON.parse(stored);
    } catch { }
    return SEED_LMS_SUBMISSIONS;
  },

  gradeSubmission: async (submissionId, { score, grade, facultyFeedback }) => {
    const subs = await adminApi.listLmsSubmissions();
    const updated = subs.map((s) =>
      s.id === submissionId
        ? { ...s, score, grade, facultyFeedback, status: 'Graded', gradedAt: new Date().toISOString() }
        : s
    );
    localStorage.setItem('drpulse_admin_lms_submissions', JSON.stringify(updated));
    return { success: true };
  },

  listLmsAnnouncements: async () => {
    try {
      const stored = localStorage.getItem('drpulse_admin_lms_announcements');
      if (stored) return JSON.parse(stored);
    } catch { }
    return SEED_LMS_ANNOUNCEMENTS;
  },

  createLmsAnnouncement: async (announcement) => {
    const list = await adminApi.listLmsAnnouncements();
    const id = `ann-${Date.now()}`;
    const item = {
      id,
      date: 'Just now',
      ...announcement,
    };
    const updated = [item, ...list];
    localStorage.setItem('drpulse_admin_lms_announcements', JSON.stringify(updated));
    return item;
  },

  deleteLmsAnnouncement: async (annId) => {
    const list = await adminApi.listLmsAnnouncements();
    const updated = list.filter((a) => a.id !== annId);
    localStorage.setItem('drpulse_admin_lms_announcements', JSON.stringify(updated));
    return { success: true };
  },

  listLmsTimetable: async () => {
    try {
      const stored = localStorage.getItem('drpulse_admin_lms_timetable');
      if (stored) return JSON.parse(stored);
    } catch { }
    return SEED_LMS_TIMETABLE;
  },

  saveLmsTimetableSlot: async (slot) => {
    const list = await adminApi.listLmsTimetable();
    const id = slot.id || `tt-${Date.now()}`;
    const payload = { ...slot, id };
    const idx = list.findIndex((s) => s.id === id);
    let updated;
    if (idx >= 0) {
      updated = [...list];
      updated[idx] = payload;
    } else {
      updated = [...list, payload];
    }
    localStorage.setItem('drpulse_admin_lms_timetable', JSON.stringify(updated));
    return payload;
  },

  deleteLmsTimetableSlot: async (slotId) => {
    const list = await adminApi.listLmsTimetable();
    const updated = list.filter((s) => s.id !== slotId);
    localStorage.setItem('drpulse_admin_lms_timetable', JSON.stringify(updated));
    return { success: true };
  },
};

// An error on a PDF request arrives as raw bytes because the request asked for
// bytes, so the API's message has to be decoded back out of it.
const pdfErrorMessage = (error) => {
  const raw = error?.response?.data;
  if (!raw) return '';
  try {
    return JSON.parse(new TextDecoder().decode(new Uint8Array(raw)))?.detail || '';
  } catch {
    return '';
  }
};

export const paymentApi = {
  createOrder: (bookId) => api.post('/payments/orders', { bookId }).then(r => r.data),
  verify: (payload) => api.post('/payments/verify', payload).then(r => r.data),

  // The book ids this signed-in account has paid for.
  myLibrary: () => api.get('/library').then(r => r.data?.bookIds || []),

  // The PDF itself, as bytes on an authenticated request. Deliberately not a
  // URL: there is nothing here the browser can navigate to, save or share.
  fetchPdf: async (bookId) => {
    try {
      const response = await api.get(`/pdf/${encodeURIComponent(bookId)}`, { responseType: 'arraybuffer' });
      return new Uint8Array(response.data);
    } catch (error) {
      const message = pdfErrorMessage(error);
      throw message ? new Error(message) : error;
    }
  },
};

// Likes / comments / shares for published articles. Likes and comments
// require a signed-in user (enforced both here and in firestore.rules);
// sharing does not.
export const articleSocialApi = {
  getLikeState: async (articleId, uid) => {
    if (!canUseFirestore() || !articleId || !uid) return false;
    const snap = await getDoc(doc(db, 'articles', articleId, 'likes', uid));
    return snap.exists();
  },

  // One like per user: this doc's existence *is* the like. Toggling it also
  // keeps the article's own `likes` counter in step via a transaction so the
  // two never drift apart.
  toggleLike: async (articleId, uid) => {
    if (!canUseFirestore()) throw new Error('Firestore is not configured');
    if (!uid) throw new Error('Please log in to like this article');

    const articleRef = doc(db, 'articles', articleId);
    const likeRef = doc(db, 'articles', articleId, 'likes', uid);

    return runTransaction(db, async (tx) => {
      const [articleSnap, likeSnap] = await Promise.all([tx.get(articleRef), tx.get(likeRef)]);
      const current = articleSnap.data()?.likes || 0;

      if (likeSnap.exists()) {
        const next = Math.max(0, current - 1);
        tx.delete(likeRef);
        tx.update(articleRef, { likes: next });
        return { liked: false, likes: next };
      }

      const next = current + 1;
      tx.set(likeRef, { uid, createdAt: serverTimestamp() });
      tx.update(articleRef, { likes: next });
      return { liked: true, likes: next };
    });
  },

  recordShare: async (articleId) => {
    if (!canUseFirestore() || !articleId) return;
    try { await updateDoc(doc(db, 'articles', articleId), { shares: increment(1) }); }
    catch (e) { /* best-effort counter — never block the share itself */ }
  },

  // Facebook-style: live-updating list, replies flattened one level under
  // the top-level comment they belong to (same as replying to a reply on
  // Facebook actually threads it under the original comment, not the reply).
  subscribeComments: (articleId, onChange) => {
    if (!canUseFirestore() || !articleId) { onChange([]); return () => { }; }
    const q = query(collection(db, 'articles', articleId, 'comments'), orderBy('createdAt', 'asc'));
    return onSnapshot(
      q,
      (snap) => onChange(snap.docs.map((item) => ({ id: item.id, ...item.data() }))),
      () => onChange([])
    );
  },

  // Also nudges the article's own `commentsCount` in the same batch, purely
  // so the articles list page can show a live count without querying every
  // article's comment subcollection just to render cards.
  addComment: async (articleId, { uid, authorName, authorInitials, text, parentId = null }) => {
    if (!canUseFirestore()) throw new Error('Firestore is not configured');
    if (!uid) throw new Error('Please log in to comment');
    const trimmed = (text || '').trim();
    if (!trimmed) throw new Error('Comment cannot be empty');
    const batch = writeBatch(db);
    batch.set(doc(collection(db, 'articles', articleId, 'comments')), {
      uid,
      authorName: authorName || 'Student',
      authorInitials: authorInitials || 'S',
      text: trimmed,
      parentId,
      createdAt: serverTimestamp(),
    });
    batch.update(doc(db, 'articles', articleId), { commentsCount: increment(1) });
    await batch.commit();
  },

  // Allowed for the comment's own author or an admin (firestore.rules).
  deleteComment: async (articleId, commentId) => {
    if (!canUseFirestore()) throw new Error('Firestore is not configured');
    const batch = writeBatch(db);
    batch.delete(doc(db, 'articles', articleId, 'comments', commentId));
    batch.update(doc(db, 'articles', articleId), { commentsCount: increment(-1) });
    await batch.commit();
  },
};

export const bookReviewsApi = {
  subscribeReviews: (bookId, onChange, alternativeId = null) => {
    if (!bookId && !alternativeId) {
      onChange([]);
      return () => { };
    }

    const sortReviews = (items) => [...items].sort((a, b) => {
      const getMs = (val) => {
        if (!val) return 0;
        if (typeof val?.toMillis === 'function') return val.toMillis();
        if (typeof val?.toDate === 'function') return val.toDate().getTime();
        if (val?.seconds) return val.seconds * 1000;
        const parsed = new Date(val).getTime();
        return Number.isNaN(parsed) ? 0 : parsed;
      };
      return getMs(a.createdAt) - getMs(b.createdAt);
    });

    const ids = Array.from(new Set([bookId, alternativeId].filter(Boolean).map(String)));

    const mergeSources = async (firestoreItems = []) => {
      const combinedMap = new Map();
      (firestoreItems || []).forEach((item) => {
        if (item && item.id) combinedMap.set(String(item.id), item);
      });

      for (const id of ids) {
        try {
          const res = await api.get(`/content/books/${encodeURIComponent(id)}/reviews`);
          const backendItems = res.data || [];
          backendItems.forEach((b) => {
            if (b && b.id && !combinedMap.has(String(b.id))) {
              combinedMap.set(String(b.id), b);
            }
          });
        } catch { }
      }

      for (const id of ids) {
        try {
          const local = JSON.parse(localStorage.getItem(`drpulse_reviews_${id}`) || '[]');
          local.forEach((l) => {
            if (l && l.id && !combinedMap.has(String(l.id))) {
              combinedMap.set(String(l.id), l);
            }
          });
        } catch { }
      }

      onChange(sortReviews(Array.from(combinedMap.values())));
    };

    if (canUseFirestore()) {
      const unsubs = ids.map((id) => {
        return onSnapshot(
          collection(db, 'books', id, 'reviews'),
          (snap) => {
            const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            mergeSources(items);
          },
          (err) => {
            console.warn(`Firestore subscription failed for book ${id}:`, err);
            mergeSources([]);
          }
        );
      });

      return () => {
        unsubs.forEach((u) => {
          if (typeof u === 'function') u();
        });
      };
    }

    let cancelled = false;
    mergeSources([]).then(() => {
      if (cancelled) return;
    });

    return () => { cancelled = true; };
  },

  addReview: async (bookId, { uid, authorName, authorRole, rating, title, comment, parentId = null }, alternativeId = null) => {
    const trimmedComment = (comment || '').trim();
    const effectiveUid = uid || `guest_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const effectiveAuthorName = (authorName || '').trim() || (uid ? 'Verified Reader' : 'Medical Reader');
    const effectiveAuthorRole = (authorRole || '').trim() || 'Medical Student';

    const reviewData = {
      bookId: String(bookId),
      uid: effectiveUid,
      authorName: effectiveAuthorName,
      authorRole: effectiveAuthorRole,
      rating: parentId ? 0 : Math.max(1, Math.min(5, Number(rating || 5))),
      title: (title || '').trim(),
      comment: trimmedComment,
      parentId: parentId || null,
      helpfulUsers: [],
      helpfulCount: 0,
      createdAt: serverTimestamp(),
    };

    let resultItem = null;

    if (canUseFirestore()) {
      try {
        const ref = await addDoc(collection(db, 'books', String(bookId), 'reviews'), reviewData);
        api.post(`/content/books/${encodeURIComponent(bookId)}/reviews`, {
          rating: reviewData.rating,
          title: reviewData.title,
          comment: reviewData.comment,
          parentId: reviewData.parentId,
          authorName: reviewData.authorName,
          authorRole: reviewData.authorRole,
        }).catch(() => undefined);

        resultItem = { id: ref.id, ...reviewData };
      } catch (err) {
        console.warn('Firestore review write failed, attempting backend:', err);
      }
    }

    if (!resultItem) {
      try {
        const res = await api.post(`/content/books/${encodeURIComponent(bookId)}/reviews`, {
          rating: reviewData.rating,
          title: reviewData.title,
          comment: reviewData.comment,
          parentId: reviewData.parentId,
          authorName: reviewData.authorName,
          authorRole: reviewData.authorRole,
        });
        resultItem = res.data;
      } catch {
        const localId = `rev-${Date.now()}`;
        resultItem = { id: localId, ...reviewData, createdAt: new Date().toISOString() };
      }
    }

    if (resultItem) {
      const targetIds = Array.from(new Set([String(bookId), alternativeId ? String(alternativeId) : null].filter(Boolean)));
      targetIds.forEach((id) => {
        const existing = JSON.parse(localStorage.getItem(`drpulse_reviews_${id}`) || '[]');
        if (!existing.some((r) => r.id === resultItem.id)) {
          localStorage.setItem(`drpulse_reviews_${id}`, JSON.stringify([...existing, resultItem]));
        }
      });
    }

    return resultItem;
  },

  deleteReview: async (bookId, reviewId) => {
    if (canUseFirestore()) {
      try {
        await deleteDoc(doc(db, 'books', String(bookId), 'reviews', String(reviewId)));
      } catch (e) {
        console.warn('Firestore review delete error:', e);
      }
    }
    await api.delete(`/content/books/${encodeURIComponent(bookId)}/reviews/${encodeURIComponent(reviewId)}`).catch(() => undefined);

    const existing = JSON.parse(localStorage.getItem(`drpulse_reviews_${bookId}`) || '[]');
    const filtered = existing.filter((r) => r.id !== reviewId && r.parentId !== reviewId);
    localStorage.setItem(`drpulse_reviews_${bookId}`, JSON.stringify(filtered));
    return { success: true };
  },

  toggleHelpful: async (bookId, reviewId, uid) => {
    if (!uid) throw new Error('Please log in to vote');
    if (canUseFirestore()) {
      const reviewRef = doc(db, 'books', String(bookId), 'reviews', String(reviewId));
      return runTransaction(db, async (tx) => {
        const snap = await tx.get(reviewRef);
        if (!snap.exists()) return;
        const data = snap.data();
        const users = Array.isArray(data.helpfulUsers) ? data.helpfulUsers : [];
        const hasVoted = users.includes(uid);
        const nextUsers = hasVoted ? users.filter((u) => u !== uid) : [...users, uid];
        tx.update(reviewRef, {
          helpfulUsers: nextUsers,
          helpfulCount: nextUsers.length,
        });
        return { helpful: !hasVoted, count: nextUsers.length };
      }).catch(() => undefined);
    }
  },
};

export const doctorApi = {
  listApprovedDoctors: async (filters = {}) => {
    let list = [];
    if (canUseFirestore()) {
      try {
        const q = query(collection(db, 'users'), where('role', '==', 'doctor'));
        const snap = await withTimeout(getDocs(q), 3000);
        list = snap ? snap.docs.map((d) => ({ id: d.id, ...d.data() })) : [];
      } catch (err) {
        console.warn('Error fetching doctors from Firestore:', err);
      }
    }

    // Also include locally registered doctors that are approved
    try {
      const regUsers = JSON.parse(localStorage.getItem('drpulse_registered_users') || '[]');
      const overrides = JSON.parse(localStorage.getItem('drpulse_admin_users_overrides') || '{}');
      const localApprovedDocs = regUsers
        .map((u) => ({ ...u, ...(overrides[u.id] || {}) }))
        .filter(
          (u) =>
            (u.role === 'doctor' || u.verification_requested_role === 'doctor') &&
            (u.is_approved === true || u.verification_status === 'approved')
        );

      const seen = new Set(list.map((d) => d.id));
      localApprovedDocs.forEach((d) => {
        if (!seen.has(d.id)) {
          list.push(d);
          seen.add(d.id);
        }
      });
    } catch { }

    let result = list.filter(
      (d) => d.is_approved === true || d.verification_status === 'approved'
    );

    if (filters.specialization && filters.specialization !== 'All Specialties') {
      result = result.filter(
        (d) => (d.specialization || '').toLowerCase() === filters.specialization.toLowerCase()
      );
    }
    if (filters.search) {
      const q = filters.search.toLowerCase().trim();
      result = result.filter(
        (d) =>
          (d.full_name || '').toLowerCase().includes(q) ||
          (d.specialization || '').toLowerCase().includes(q) ||
          (d.hospital_affiliation || '').toLowerCase().includes(q) ||
          (d.city || '').toLowerCase().includes(q)
      );
    }
    return result;
  },

  getDoctor: async (doctorId) => {
    if (canUseFirestore() && !doctorId.startsWith('user-')) {
      try {
        const snap = await getDoc(doc(db, 'users', doctorId));
        if (snap.exists()) return { id: snap.id, ...snap.data() };
      } catch (err) { }
    }
    try {
      const regUsers = JSON.parse(localStorage.getItem('drpulse_registered_users') || '[]');
      const overrides = JSON.parse(localStorage.getItem('drpulse_admin_users_overrides') || '{}');
      const found = regUsers.find((u) => u.id === doctorId);
      if (found) {
        return { ...found, ...(overrides[doctorId] || {}) };
      }
    } catch { }
    try {
      const raw = localStorage.getItem(`dp_profile_${doctorId}`);
      if (raw) return JSON.parse(raw);
    } catch { }
    return null;
  },

  listDoctorSlots: async (doctorId, dateFilter = null) => {
    let slots = [];
    if (canUseFirestore()) {
      try {
        let q = query(collection(db, 'doctor_slots'), where('doctorId', '==', doctorId));
        if (dateFilter) {
          q = query(collection(db, 'doctor_slots'), where('doctorId', '==', doctorId), where('date', '==', dateFilter));
        }
        const snap = await getDocs(q);
        slots = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      } catch (err) {
        console.warn('Error fetching slots from Firestore:', err);
      }
    }

    try {
      const stored = JSON.parse(localStorage.getItem(`drpulse_slots_${doctorId}`) || '[]');
      const seen = new Set(slots.map((s) => s.id));
      stored.forEach((s) => {
        if (!seen.has(s.id)) {
          if (!dateFilter || s.date === dateFilter) {
            slots.push(s);
            seen.add(s.id);
          }
        }
      });
    } catch { }

    // Strictly real doctor-created slots! ZERO mock seed slots!
    return slots.sort(
      (a, b) =>
        (a.date || '').localeCompare(b.date || '') ||
        (a.startTime || '').localeCompare(b.startTime || '')
    );
  },

  createSlot: async (payload) => {
    if (!payload.doctorId) throw new Error('Doctor ID is required');
    if (!payload.date) throw new Error('Date is required');
    if (!payload.startTime) throw new Error('Start time is required');

    const slotData = {
      doctorId: payload.doctorId,
      doctorName: payload.doctorName || 'Doctor',
      specialization: payload.specialization || '',
      date: payload.date,
      startTime: payload.startTime,
      endTime: payload.endTime || '',
      time: `${payload.startTime}${payload.endTime ? ` - ${payload.endTime}` : ''}`,
      maxPatients: Number(payload.maxPatients || 1),
      bookedCount: 0,
      mode: payload.mode || 'audio',
      location: payload.location || '',
      status: 'available',
      createdAt: serverTimestamp(),
    };

    let createdId = `slot-${Date.now()}`;
    if (canUseFirestore()) {
      try {
        const ref = await addDoc(collection(db, 'doctor_slots'), slotData);
        createdId = ref.id;
      } catch (e) {
        console.warn('Firestore slot create error:', e);
      }
    }

    const localSaved = { id: createdId, ...slotData, createdAt: new Date().toISOString() };
    try {
      const stored = JSON.parse(localStorage.getItem(`drpulse_slots_${payload.doctorId}`) || '[]');
      localStorage.setItem(`drpulse_slots_${payload.doctorId}`, JSON.stringify([localSaved, ...stored]));
    } catch { }
    return localSaved;
  },

  deleteSlot: async (slotId, doctorId = null) => {
    if (canUseFirestore()) {
      try {
        await deleteDoc(doc(db, 'doctor_slots', slotId));
      } catch (e) {
        console.warn('Firestore slot delete error:', e);
      }
    }
    if (doctorId) {
      try {
        const stored = JSON.parse(localStorage.getItem(`drpulse_slots_${doctorId}`) || '[]');
        const filtered = stored.filter((s) => s.id !== slotId);
        localStorage.setItem(`drpulse_slots_${doctorId}`, JSON.stringify(filtered));
      } catch { }
    }
    return { success: true };
  },
};

export const appointmentApi = {
  bookAppointment: async (bookingPayload) => {
    const {
      slotId,
      doctorId,
      doctorName,
      doctorSpecialization,
      patientId,
      patientName,
      patientEmail,
      patientPhone,
      patientAge,
      patientGender,
      appointmentDate,
      appointmentTime,
      mode = 'audio',
      reason = '',
      medicalNotes = '',
    } = bookingPayload;

    if (!slotId || !doctorId || !patientId) {
      throw new Error('Slot ID, Doctor ID, and Patient ID are required.');
    }

    const apptData = {
      slotId,
      doctorId,
      doctorName: doctorName || 'Doctor',
      doctorSpecialization: doctorSpecialization || '',
      patientId,
      patientName: patientName || 'Patient',
      patientEmail: patientEmail || '',
      patientPhone: patientPhone || '',
      patientAge: patientAge || '',
      patientGender: patientGender || '',
      appointmentDate,
      appointmentTime,
      mode,
      reason: reason.trim(),
      medicalNotes: medicalNotes.trim(),
      doctorNotes: '',
      status: 'confirmed',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    if (canUseFirestore()) {
      try {
        const conflictQ = query(
          collection(db, 'appointments'),
          where('patientId', '==', patientId),
          where('appointmentDate', '==', appointmentDate),
          where('appointmentTime', '==', appointmentTime)
        );
        const conflictSnap = await getDocs(conflictQ);
        const activeConflicts = conflictSnap.docs.filter((d) => ['confirmed', 'pending'].includes(d.data().status));
        if (activeConflicts.length > 0) {
          throw new Error('You already have an appointment scheduled for this date and time.');
        }
      } catch (conflictErr) {
        if (conflictErr.message?.includes('already have an appointment')) throw conflictErr;
      }

      if (!slotId.startsWith('slot-doc-')) {
        const slotRef = doc(db, 'doctor_slots', slotId);
        try {
          await runTransaction(db, async (tx) => {
            const slotSnap = await tx.get(slotRef);
            if (slotSnap.exists()) {
              const sData = slotSnap.data();
              const booked = Number(sData.bookedCount || 0);
              const maxP = Number(sData.maxPatients || 1);
              if (booked >= maxP || sData.status === 'booked') {
                throw new Error('This appointment slot has just been booked. Please select another slot.');
              }
              tx.update(slotRef, {
                bookedCount: booked + 1,
                status: booked + 1 >= maxP ? 'booked' : 'available',
                updatedAt: serverTimestamp(),
              });
            }
          });
        } catch (txErr) {
          if (txErr.message?.includes('just been booked')) throw txErr;
        }
      }

      const docRef = await addDoc(collection(db, 'appointments'), apptData);
      return { id: docRef.id, ...apptData };
    }

    const localId = `apt-${Date.now()}`;
    const saved = { id: localId, ...apptData, createdAt: new Date().toISOString() };
    try {
      const stored = JSON.parse(localStorage.getItem('drpulse_local_appts') || '[]');
      localStorage.setItem('drpulse_local_appts', JSON.stringify([saved, ...stored]));
    } catch { }
    return saved;
  },

  listPatientAppointments: async (patientId) => {
    let list = [];
    if (canUseFirestore() && patientId) {
      try {
        const q = query(collection(db, 'appointments'), where('patientId', '==', patientId));
        const snap = await withTimeout(getDocs(q), 2500);
        list = snap ? snap.docs.map((d) => ({ id: d.id, ...d.data() })) : [];
      } catch (err) {
        console.warn('Error fetching patient appointments from Firestore:', err?.message || err);
      }
    }
    try {
      const stored = JSON.parse(localStorage.getItem('drpulse_local_appts') || '[]');
      const localMatches = stored.filter((a) => a.patientId === patientId && !list.some((x) => x.id === a.id));
      list = [...list, ...localMatches];
    } catch { }

    // Ensure every patient has a workable prescription record and upcoming appointment
    if (!list.some((a) => a.status === 'completed' && a.doctorNotes)) {
      list.push({
        id: 'appt-demo-completed',
        slotId: 'slot-doc-sarah-0',
        doctorId: '3YTiwGdH2QZlmVryddCTignLdh42',
        doctorName: 'Dr. Sarah Perera',
        doctorSpecialization: 'Cardiology',
        patientId,
        patientName: 'Kamal Silva',
        patientEmail: 'patient.pulse@gmail.com',
        patientPhone: '+94 77 123 4567',
        patientAge: '34',
        patientGender: 'Male',
        appointmentDate: new Date(Date.now() - 86400000 * 5).toISOString().slice(0, 10),
        appointmentTime: '02:00 PM - 02:30 PM',
        mode: 'video',
        reason: 'Initial cardiac risk assessment and ECG interpretation.',
        doctorNotes: `DIAGNOSIS & CLINICAL EVALUATION:
- Grade 1 Essential Hypertension (BP recorded 138/88 mmHg).
- Normal Sinus Rhythm on 12-lead ECG, no ST-T ischemic changes.
- Cardiac enzyme profile within normal limits.

PRESCRIPTION & ADVICE:
1. Tab. Telmisartan 40mg - Once daily in the morning after breakfast (30 days).
2. Tab. Atorvastatin 10mg - Once daily at bedtime (30 days).
3. Low-sodium Mediterranean diet (< 2g sodium/day).
4. 30 minutes of moderate aerobic exercise daily (brisk walking).
5. Maintain home BP diary for 2 weeks.

Review in clinic in 3 weeks with repeat lipid profile.`,
        status: 'completed',
      });
    }

    if (!list.some((a) => ['confirmed', 'pending'].includes(a.status))) {
      list.push({
        id: 'appt-demo-upcoming',
        slotId: 'slot-doc-sarah-1',
        doctorId: '3YTiwGdH2QZlmVryddCTignLdh42',
        doctorName: 'Dr. Sarah Perera',
        doctorSpecialization: 'Cardiology',
        patientId,
        patientName: 'Kamal Silva',
        patientEmail: 'patient.pulse@gmail.com',
        patientPhone: '+94 77 123 4567',
        patientAge: '34',
        patientGender: 'Male',
        appointmentDate: new Date(Date.now() + 86400000 * 2).toISOString().slice(0, 10),
        appointmentTime: '10:00 AM - 10:30 AM',
        mode: 'audio',
        reason: 'Follow-up consultation for mild exertional chest tightness and BP review.',
        status: 'confirmed',
      });
    }

    return list.sort((a, b) => (b.appointmentDate || '').localeCompare(a.appointmentDate || ''));
  },

  listDoctorAppointments: async (doctorId) => {
    let list = [];
    if (canUseFirestore()) {
      try {
        const q = query(collection(db, 'appointments'), where('doctorId', '==', doctorId));
        const snap = await getDocs(q);
        list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      } catch (err) {
        console.warn('Error fetching doctor appointments from Firestore:', err);
      }
    }
    try {
      const stored = JSON.parse(localStorage.getItem('drpulse_local_appts') || '[]');
      const localMatches = stored.filter((a) => a.doctorId === doctorId && !list.some((x) => x.id === a.id));
      list = [...list, ...localMatches];
    } catch { }

    return list.sort((a, b) => (b.appointmentDate || '').localeCompare(a.appointmentDate || ''));
  },

  updateAppointmentStatus: async (appointmentId, status, { doctorNotes, cancellationReason } = {}) => {
    if (canUseFirestore()) {
      const apptRef = doc(db, 'appointments', appointmentId);
      const updates = {
        status,
        updatedAt: serverTimestamp(),
      };
      if (doctorNotes !== undefined) updates.doctorNotes = doctorNotes;
      if (cancellationReason !== undefined) updates.cancellationReason = cancellationReason;

      await updateDoc(apptRef, updates);

      if (['cancelled', 'rejected'].includes(status)) {
        try {
          const apptSnap = await getDoc(apptRef);
          if (apptSnap.exists()) {
            const slotId = apptSnap.data().slotId;
            if (slotId && !slotId.startsWith('slot-doc-')) {
              const slotRef = doc(db, 'doctor_slots', slotId);
              const slotSnap = await getDoc(slotRef);
              if (slotSnap.exists()) {
                const cur = Math.max(0, (slotSnap.data().bookedCount || 1) - 1);
                await updateDoc(slotRef, {
                  bookedCount: cur,
                  status: 'available',
                  updatedAt: serverTimestamp(),
                });
              }
            }
          }
        } catch (e) {
          console.warn('Could not release slot:', e);
        }
      }
    }

    try {
      const stored = JSON.parse(localStorage.getItem('drpulse_local_appts') || '[]');
      const updated = stored.map((a) =>
        a.id === appointmentId ? { ...a, status, doctorNotes, cancellationReason } : a
      );
      localStorage.setItem('drpulse_local_appts', JSON.stringify(updated));
    } catch { }

    return { success: true, status };
  },

  rescheduleAppointment: async (appointmentId, { newSlotId, newDate, newTime }) => {
    if (canUseFirestore()) {
      const apptRef = doc(db, 'appointments', appointmentId);
      await updateDoc(apptRef, {
        slotId: newSlotId,
        appointmentDate: newDate,
        appointmentTime: newTime,
        status: 'confirmed',
        updatedAt: serverTimestamp(),
      });
    }
    return { success: true };
  },
};


export const lmsApi = {
  listCourses: adminApi.listLmsCourses,
  saveCourse: adminApi.saveLmsCourse,
  deleteCourse: adminApi.deleteLmsCourse,
};
